import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AccountInfo, Session } from '@sl/core';
import { Icon, Menu, type MenuEntry } from '../../ui';
import { OnboardingLauncher } from '../../onboarding/OnboardingLauncher';
import { workspaceRequest } from '../../routes/acquisition/identity';
import { useAppConfig } from '../configContext';
import { goToServer, HOME_PATH } from '../server';
import { rememberAccount } from './selectedAccount';
import {
  accountDisplayName,
  DEMO_ACCOUNT_LABEL,
  isDemoAccount,
  useDemoAccount,
} from './demoAccount';
import styles from './AppShell.module.css';

export function ConnectedAccountSwitch({
  account,
  initials,
  session,
  onSelect,
  onConnected,
}: {
  account: AccountInfo;
  initials: string;
  session: Session;
  onSelect: (accountId: string) => void;
  onConnected: (accountId: string) => Promise<void>;
}) {
  const anchor = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');
  const config = useAppConfig();
  const demo = useDemoAccount();
  const inDemo = config.transport === 'demo';
  const workspace = useQuery({
    queryKey: ['account-picker', 'workspace', session.user.id],
    queryFn: () => workspaceRequest<{ data: { accounts: AccountInfo[] } }>(),
    enabled: inDemo && /^usr_[0-9a-f]{32}$/.test(session.user.id),
    retry: false,
    refetchOnWindowFocus: true,
  });
  const realAccounts = (inDemo ? (workspace.data?.data.accounts ?? []) : session.accounts).filter(
    (item) => !isDemoAccount(item),
  );
  // A bookmarked demo URL also respects removal, including on another browser/device.
  useEffect(() => {
    if (inDemo && demo.hidden) goToServer('api', HOME_PATH);
  }, [inDemo, demo.hidden]);
  const openRealAccount = (id: string) => {
    if (inDemo) {
      rememberAccount(id);
      goToServer('api', HOME_PATH);
    } else onSelect(id);
  };
  const removeDemo = async () => {
    setRemoving(true);
    setError('');
    try {
      await demo.setHidden(true);
    } catch {
      setError('Couldn’t remove the demo. Please try again.');
    } finally {
      setRemoving(false);
    }
  };
  const displayName = accountDisplayName(account);
  const items: MenuEntry[] = [
    { type: 'label', key: 'heading', label: 'Your ad accounts' },
    ...realAccounts.map((item, index) => ({
      key: `account-${index}`,
      label: item.name,
      icon: !inDemo && item.id === account.id ? ('check' as const) : ('layers' as const),
      end:
        !inDemo && item.id === account.id
          ? 'Current account'
          : `${item.currency} · ${item.timezone}`,
      onSelect: () => openRealAccount(item.id),
    })),
    {
      key: 'workspace',
      icon: 'users',
      label: 'Your workspace',
      end: realAccounts.length
        ? 'Manage your Facebook accounts'
        : 'Connect your Facebook ad account',
      onSelect: () => goToServer('api', '/account'),
    },
    ...(inDemo && workspace.isError
      ? [
          {
            key: 'retry',
            icon: 'refresh' as const,
            label: 'Retry loading your ad accounts',
            onSelect: () => {
              void workspace.refetch();
            },
          },
        ]
      : []),
    ...(inDemo && workspace.isFetching && !workspace.data
      ? [
          {
            key: 'loading',
            icon: 'loader' as const,
            label: 'Loading your ad accounts…',
            disabled: true,
            onSelect: () => {},
          },
        ]
      : []),
    ...(!demo.isPending && !demo.isError && !demo.hidden
      ? [
          { type: 'separator' as const, key: 'demo-separator' },
          { type: 'label' as const, key: 'demo-heading', label: 'Demo · sample data only' },
          {
            key: 'demo',
            label: DEMO_ACCOUNT_LABEL,
            icon: 'flask' as const,
            end: inDemo ? 'Current · fictional shop, no real ads' : 'Explore with sample data',
            onSelect: () => {
              if (!inDemo) goToServer('demo', HOME_PATH);
            },
          },
          ...(demo.identity
            ? [
                {
                  key: 'remove-demo',
                  label: removing ? 'Removing demo…' : 'Remove demo account',
                  icon: 'trash' as const,
                  end: 'Saved to your profile; your real accounts stay',
                  disabled: removing,
                  onSelect: () => {
                    void removeDemo();
                  },
                },
              ]
            : []),
        ]
      : []),
    { type: 'separator', key: 'separator' },
    {
      key: 'attach',
      icon: 'plus',
      label: 'Attach a new Facebook ad account',
      onSelect: () => {
        if (inDemo) goToServer('api', '/account');
        else setConnecting(true);
      },
    },
  ];
  return (
    <>
      <button
        ref={anchor}
        className={styles.account}
        type="button"
        aria-label={`${displayName} — Switch ad account`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen(!open)}
        data-testid="shell.account-switch"
      >
        <span className={styles.avatar} aria-hidden="true">
          {initials}
        </span>
        <span className={styles.accountName} data-testid="shell.account-name">
          {displayName}
        </span>
        <Icon name="chevron-down" size={14} className={styles.accountCaret} />
      </button>
      {error ? <span role="alert">{error}</span> : null}
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        id={menuId}
        items={items}
        placement="bottom-start"
        width={340}
        className={styles.accountMenu}
        aria-label="Your ad accounts"
        testID="shell.account-switch.menu"
      />
      <OnboardingLauncher
        additional
        open={connecting}
        onClose={() => setConnecting(false)}
        onConnected={async (id) => {
          await onConnected(id);
          setConnecting(false);
        }}
      />
    </>
  );
}
